import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { EmptyState, PageHeader, Spinner } from "@/components/bits";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarClock, Loader2, Pencil, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_AG = {
  ativo: { label: "Ativo", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
  futuro: { label: "Futuro", cls: "bg-indigo-500/10 text-indigo-400 border-indigo-500/30" },
  encerrado: { label: "Encerrado", cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30" },
};

const p2 = (n) => String(n).padStart(2, "0");
const hojeISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
};
const parseLocal = (v) => {
  if (!v) return null;
  const d = new Date(String(v).trim().replace(" ", "T"));
  return isNaN(d) ? null : d;
};
const statusAgenda = (it) => {
  const ini = parseLocal(it.permanencia_inicial);
  const fim = parseLocal(it.permanencia_final);
  if (!ini && !fim) return null;
  const agora = new Date();
  if (fim && agora > fim) return "encerrado";
  if (ini && agora < ini) return "futuro";
  return "ativo";
};
const fmtDT = (iso) => {
  const d = parseLocal(iso);
  if (!d) return "—";
  return `${p2(d.getDate())}/${p2(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
};
const paraInput = (iso) => {
  const d = parseLocal(iso);
  if (!d) return "";
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}T${p2(d.getHours())}:${p2(d.getMinutes())}`;
};

const formVazio = {
  nome: "", cpf: "", ddi_celular: "+55", celular: "", empresa_nome: "",
  pessoa_visitada_nome: "", motivo: "", permanencia_inicial: "", permanencia_final: "",
  placa: "", modelo_veiculo: "", cor_veiculo: "", observacao: "",
};

export default function AgendamentosPage() {
  const [dados, setDados] = useState(null);
  const [eql, setEql] = useState("");
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const [erro, setErro] = useState("");
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(formVazio);
  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState(null);
  const [processando, setProcessando] = useState(false);

  const carregar = useCallback(async () => {
    setErro("");
    try {
      const params = {};
      if (eql.trim()) params.eql = eql.trim();
      if (de) params.data_inicio = de;
      if (ate) params.data_fim = ate;
      const { data } = await api.get("/tenant/agendamento/visitantes", { params });
      setDados(data);
    } catch (e) {
      setErro(errMsg(e));
      setDados({ visitantes: [], total: 0 });
    }
  }, [eql, de, ate]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirEdicao = (it) => {
    setEditando(it);
    setForm({
      nome: it.nome || "", cpf: it.cpf || "", ddi_celular: it.ddi_celular || "+55",
      celular: it.celular || "", empresa_nome: it.empresa_nome || "",
      pessoa_visitada_nome: it.pessoa_visitada_nome || "", motivo: it.motivo || "",
      permanencia_inicial: paraInput(it.permanencia_inicial),
      permanencia_final: paraInput(it.permanencia_final),
      placa: it.placa || "", modelo_veiculo: it.modelo_veiculo || "",
      cor_veiculo: it.cor_veiculo || "", observacao: it.observacao || "",
    });
  };

  const salvar = async () => {
    if (!editando || salvando) return;
    setSalvando(true);
    try {
      await api.put(`/tenant/agendamento/visitante/${editando.id}`, {
        origem: "-", eql: editando.unidade || "", cpf_morador: "-", usuario_cadastro: "-",
        cpf: form.cpf.replace(/\D/g, ""), nome: form.nome.trim(),
        celular: form.celular.replace(/\D/g, ""), ddi_celular: form.ddi_celular || "+55",
        motivo: form.motivo.trim(),
        permanencia_inicial: form.permanencia_inicial ? `${form.permanencia_inicial}:00` : "",
        permanencia_final: form.permanencia_final ? `${form.permanencia_final}:00` : "",
        local_acesso_nome: "",
        pessoa_visitada_nome: form.pessoa_visitada_nome.trim(),
        empresa_nome: form.empresa_nome.trim(),
        placa: form.placa.trim().toUpperCase(),
        modelo_veiculo: form.modelo_veiculo.trim(), cor_veiculo: form.cor_veiculo.trim(),
        observacao: form.observacao.trim(),
      });
      toast.success("Agendamento atualizado.");
      setEditando(null);
      carregar();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSalvando(false); }
  };

  const confirmarExcluir = async () => {
    if (!excluindo || processando) return;
    setProcessando(true);
    try {
      await api.delete(`/tenant/agendamento/visitante/${excluindo.id}`);
      toast.success(`Agendamento de ${excluindo.nome || "visitante"} excluído.`);
      setExcluindo(null);
      carregar();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setProcessando(false); }
  };

  const itens = dados?.visitantes || [];
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="agendamentos-page">
      <PageHeader title="Manutenção de Agendamentos"
        subtitle="Pré-agendas de visitantes e prestadores de todo o empreendimento (appmobile)">
        <Button variant="outline" size="sm" onClick={carregar} data-testid="agendamentos-atualizar"
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
          <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
        </Button>
      </PageHeader>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4 mb-4 flex flex-wrap items-end gap-4"
        data-testid="agendamentos-filtros">
        <div>
          <Label className="text-xs text-zinc-400">Unidade / EQL</Label>
          <Input data-testid="agendamentos-filtro-eql" value={eql}
            onChange={(e) => setEql(e.target.value.toUpperCase())} placeholder="Todas"
            className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 w-36 uppercase" />
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Início a partir de</Label>
          <Input type="date" data-testid="agendamentos-filtro-de" value={de}
            onChange={(e) => setDe(e.target.value)}
            className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 w-40" />
        </div>
        <div>
          <Label className="text-xs text-zinc-400">até</Label>
          <Input type="date" data-testid="agendamentos-filtro-ate" value={ate}
            onChange={(e) => setAte(e.target.value)}
            className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 w-40" />
        </div>
      </div>

      {!dados && <Spinner />}
      {dados && erro && (
        <EmptyState icon={CalendarClock} title="Não foi possível carregar os agendamentos" subtitle={erro} />
      )}
      {dados && !erro && itens.length === 0 && (
        <EmptyState icon={CalendarClock} title="Nenhum agendamento encontrado"
          subtitle="Agendamentos criados pelos moradores no appmobile (Pré Agenda) aparecem aqui." />
      )}
      {dados && !erro && itens.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-x-auto">
          <table className="w-full text-sm" data-testid="agendamentos-tabela">
            <thead>
              <tr className="text-left text-xs text-zinc-400 border-b border-zinc-800">
                <th className="px-4 py-3 font-medium">Visitante</th>
                <th className="px-4 py-3 font-medium">Unidade</th>
                <th className="px-4 py-3 font-medium">Permanência</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Empresa</th>
                <th className="px-4 py-3 font-medium">Cadastrado por</th>
                <th className="px-4 py-3 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((it) => {
                const sk = statusAgenda(it);
                const st = sk ? STATUS_AG[sk] : null;
                return (
                  <tr key={it.id} className="border-b border-zinc-800/60 last:border-0"
                    data-testid={`agendamentos-row-${it.id}`}>
                    <td className="px-4 py-3 text-zinc-100 font-medium">
                      {it.nome || "(sem nome)"}
                      {it.cpf && <span className="block text-[11px] text-zinc-500 font-normal">{it.cpf}</span>}
                    </td>
                    <td className="px-4 py-3 text-zinc-300">{it.unidade || "—"}</td>
                    <td className="px-4 py-3 text-zinc-300 whitespace-nowrap">
                      {fmtDT(it.permanencia_inicial)}{" → "}{fmtDT(it.permanencia_final)}
                    </td>
                    <td className="px-4 py-3">
                      {st ? (
                        <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-medium ${st.cls}`}
                          data-testid={`agendamentos-status-${it.id}`}>{st.label}</span>
                      ) : <span className="text-zinc-500">—</span>}
                    </td>
                    <td className="px-4 py-3 text-zinc-300">{it.empresa_nome || "—"}</td>
                    <td className="px-4 py-3 text-zinc-300">{it.usuario_cadastro || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" data-testid={`agendamentos-editar-${it.id}`}
                          onClick={() => abrirEdicao(it)} aria-label="Alterar"
                          className="border-zinc-700 text-indigo-300 hover:bg-zinc-800 h-8 px-2">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="outline" size="sm" data-testid={`agendamentos-excluir-${it.id}`}
                          onClick={() => setExcluindo(it)} aria-label="Excluir"
                          className="border-red-900/60 text-red-400 hover:bg-red-950/40 h-8 px-2">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editando} onOpenChange={(o) => !o && !salvando && setEditando(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto"
          data-testid="agendamentos-editar-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Alterar agendamento</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Unidade <b className="text-zinc-200">{editando?.unidade || "—"}</b>. A alteração é gravada direto no Brave Access.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-1">
            <div className="col-span-2">
              <Label className="text-xs text-zinc-400">Nome do visitante</Label>
              <Input value={form.nome} onChange={set("nome")} data-testid="agendamentos-form-nome"
                className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">CPF</Label>
              <Input value={form.cpf} onChange={set("cpf")} data-testid="agendamentos-form-cpf"
                className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Celular</Label>
              <div className="flex gap-2 mt-1">
                <Input value={form.ddi_celular} onChange={set("ddi_celular")} data-testid="agendamentos-form-ddi"
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 w-20" />
                <Input value={form.celular} onChange={set("celular")} data-testid="agendamentos-form-celular"
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 flex-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Empresa</Label>
              <Input value={form.empresa_nome} onChange={set("empresa_nome")} data-testid="agendamentos-form-empresa"
                className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Pessoa visitada</Label>
              <Input value={form.pessoa_visitada_nome} onChange={set("pessoa_visitada_nome")}
                data-testid="agendamentos-form-pessoa"
                className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-zinc-400">Motivo</Label>
              <Input value={form.motivo} onChange={set("motivo")} data-testid="agendamentos-form-motivo"
                className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Início da permanência</Label>
              <Input type="datetime-local" value={form.permanencia_inicial}
                onChange={set("permanencia_inicial")} data-testid="agendamentos-form-inicio"
                className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Fim da permanência</Label>
              <Input type="datetime-local" value={form.permanencia_final}
                onChange={set("permanencia_final")} data-testid="agendamentos-form-fim"
                className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Placa</Label>
              <Input value={form.placa} onChange={(e) => setForm((f) => ({ ...f, placa: e.target.value.toUpperCase() }))}
                data-testid="agendamentos-form-placa"
                className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 uppercase" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-zinc-400">Modelo</Label>
                <Input value={form.modelo_veiculo} onChange={set("modelo_veiculo")}
                  data-testid="agendamentos-form-modelo"
                  className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Cor</Label>
                <Input value={form.cor_veiculo} onChange={set("cor_veiculo")} data-testid="agendamentos-form-cor"
                  className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" />
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-zinc-400">Observação</Label>
              <Input value={form.observacao} onChange={set("observacao")} data-testid="agendamentos-form-obs"
                className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-3">
            <Button variant="outline" data-testid="agendamentos-editar-cancelar"
              onClick={() => setEditando(null)} disabled={salvando}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
              Cancelar
            </Button>
            <Button data-testid="agendamentos-editar-salvar" onClick={salvar} disabled={salvando}
              className="bg-indigo-600 hover:bg-indigo-500 text-white">
              {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar alterações"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!excluindo} onOpenChange={(o) => !o && !processando && setExcluindo(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800" data-testid="agendamentos-excluir-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Excluir agendamento</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Excluir o agendamento de <b className="text-zinc-200">{excluindo?.nome || "(sem nome)"}</b>
              {excluindo?.unidade ? <> da unidade <b className="text-zinc-200">{excluindo.unidade}</b></> : null}?
              A exclusão é feita no Brave Access e fica registrada na auditoria.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-2">
            <Button variant="outline" data-testid="agendamentos-excluir-cancelar"
              onClick={() => setExcluindo(null)} disabled={processando}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
              Cancelar
            </Button>
            <Button data-testid="agendamentos-excluir-confirmar" onClick={confirmarExcluir}
              disabled={processando} className="bg-red-600 hover:bg-red-500 text-white">
              {processando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sim, excluir"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
